import { useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { ArrowUpDown, ArrowUp, ArrowDown, ListChecks } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { formatInt, formatRand, type ShiftRow } from '../../utils/performance'

const LEFT_COLS = new Set(['shift_date', 'driver', 'vehicle', 'shift_window', 'shift_status'])
const isRight = (id: string) => !LEFT_COLS.has(id)

const STATUS_STYLES: Record<string, string> = {
  validated: 'bg-muted text-muted-foreground',
  submitted:
    'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  flagged:
    'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  cancelled:
    'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
}

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? 'bg-muted text-muted-foreground'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize',
        cls,
      )}
    >
      {status}
    </span>
  )
}

export function ShiftTable({
  rows,
  total,
  loading,
  onLoadMore,
  canLoadMore,
}: {
  rows: ShiftRow[]
  total: number
  loading: boolean
  onLoadMore: () => void
  canLoadMore: boolean
}) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'shift_date', desc: true }])

  const columns = useMemo<ColumnDef<ShiftRow>[]>(
    () => [
      {
        accessorKey: 'shift_date',
        header: 'Date',
        cell: (c) => <span className="tabular-nums text-foreground">{c.getValue<string>()}</span>,
      },
      {
        accessorKey: 'driver',
        header: 'Driver',
        cell: (c) => <span className="font-medium text-foreground">{c.getValue<string>()}</span>,
      },
      {
        accessorKey: 'vehicle',
        header: 'Vehicle',
        cell: (c) => (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{c.getValue<string>()}</span>
            <span className="text-xs text-muted-foreground">{c.row.original.vehicle_model}</span>
          </div>
        ),
      },
      {
        accessorKey: 'shift_window',
        header: 'Window',
        cell: (c) => <span className="text-muted-foreground">{c.getValue<string>()}</span>,
      },
      {
        accessorKey: 'shift_status',
        header: 'Status',
        cell: (c) => <StatusBadge status={c.getValue<string>()} />,
      },
      {
        accessorKey: 'uber_earnings_zar',
        header: 'Uber earnings',
        cell: (c) => <span className="tabular-nums">{formatRand(c.getValue<number>())}</span>,
      },
      {
        accessorKey: 'petrol_cost_zar',
        header: 'Petrol',
        cell: (c) => <span className="tabular-nums">{formatRand(c.getValue<number>())}</span>,
      },
      {
        accessorKey: 'kilometres',
        header: 'Kms',
        cell: (c) => <span className="tabular-nums">{formatInt(c.getValue<number>())}</span>,
      },
      {
        accessorKey: 'earnings_per_km_zar',
        header: 'R/km',
        cell: (c) => {
          const v = c.getValue<number | null>()
          return (
            <span className="tabular-nums">{v === null ? '—' : formatRand(v)}</span>
          )
        },
      },
    ],
    [],
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="rounded-lg border border-border bg-card shadow-retool-sm">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <ListChecks className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Shifts</h2>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {loading && rows.length === 0
            ? '…'
            : `showing ${formatInt(rows.length)} of ${formatInt(total)}`}
        </span>
      </div>
      <div className="max-h-[520px] overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const right = isRight(header.column.id)
                  const sorted = header.column.getIsSorted()
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'cursor-pointer select-none whitespace-nowrap',
                        right && 'text-right',
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span
                        className={cn(
                          'inline-flex items-center gap-1',
                          right && 'flex-row-reverse',
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : sorted === 'desc' ? (
                          <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </span>
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  No shifts for the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const right = isRight(cell.column.id)
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn('whitespace-nowrap align-top', right && 'text-right')}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {canLoadMore && (
        <div className="flex items-center justify-center border-t border-border px-4 py-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={loading}
            className="gap-2"
          >
            {loading && <Spinner className="h-3.5 w-3.5" />}
            Load more
          </Button>
        </div>
      )}
    </div>
  )
}
